import { useMemo, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createCustomerSchema, CustomerFormData } from '@/schemas/customer.schema';
import { Customer } from '@/types/customer';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import type { NormalizedAddress } from '@/utils/parseGoogleAddress';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface CustomerFormProps {
  customer: Customer;
  onSubmit: (data: CustomerFormData) => void;
  /** Fields are always open for anyone allowed to edit; false renders read-only. */
  canEdit: boolean;
  isSaving?: boolean;
}

/** The saved row, as form values. */
function toFormValues(customer: Customer): CustomerFormData {
  return {
    customer_type: customer.customer_type,
    customer_name: customer.customer_name,
    company_name: customer.company_name || '',
    primary_phone: customer.primary_phone,
    secondary_phone: customer.secondary_phone || '',
    primary_email: customer.primary_email || '',
    secondary_email: customer.secondary_email || '',
    primary_address: customer.primary_address,
    suburb: (customer as any).suburb || '',
    state: (customer as any).state || '',
    postcode: (customer as any).postcode || '',
    secondary_address: customer.secondary_address || '',
    tags: customer.tags || [],
    notes: customer.notes || '',
  };
}

export function CustomerForm({ customer, onSubmit, canEdit, isSaving = false }: CustomerFormProps) {
  const { locale } = useCompanyLocale();
  const schema = useMemo(() => createCustomerSchema(locale), [locale]);
  const { addressLabels, addressPlaceholders, postcode: postcodeRule } = locale;

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(customer),
  });

  const customerType = form.watch('customer_type');
  const isDirty = form.formState.isDirty;

  // True from the moment we hand values to onSubmit until the saved row
  // arrives back, so the refetch is allowed to overwrite the dirty form.
  const awaitingSave = useRef(false);

  // Re-sync with the server row whenever it actually changes. Keyed on
  // updated_at, so a background refetch that returns identical data is a no-op
  // and never clobbers half-typed edits.
  //
  // This replaces a remount-on-save: the remount used to fire before the
  // refetch resolved, so the form reloaded the PRE-save row and a newly added
  // email looked like it had vanished (it was in the database all along).
  const savedVersion = `${customer.id}:${customer.updated_at ?? ''}`;
  useEffect(() => {
    // Someone else's change while this user is mid-edit: leave their typing be.
    if (form.formState.isDirty && !awaitingSave.current) return;
    awaitingSave.current = false;
    form.reset(toFormValues(customer));
    // form.reset and the row object are stable for a given savedVersion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedVersion]);

  // Warn on tab close / reload while there are unsaved edits.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // Called when user selects a Google Places suggestion.
  // Uses setIfChanged to prevent false "Unsaved Changes" state.
  // isPartial guard: never overwrite existing non-empty field with empty string.
  const handleAddressSelected = (normalized: NormalizedAddress) => {
    const setIfChanged = (name: keyof CustomerFormData, newVal: string) => {
      if (form.getValues(name) !== newVal) {
        form.setValue(name, newVal as never, { shouldDirty: true });
      }
    };
    setIfChanged('primary_address', normalized.street);
    if (normalized.suburb)   setIfChanged('suburb', normalized.suburb);
    if (normalized.state)    setIfChanged('state', normalized.state);
    if (normalized.postcode) setIfChanged('postcode', normalized.postcode);

    // Coordinates: silent, no dirty flag
    if (normalized.lat != null) form.setValue('lat', normalized.lat, { shouldDirty: false });
    if (normalized.lng != null) form.setValue('lng', normalized.lng, { shouldDirty: false });
  };

  return (
    <Form {...form}>
      <form
        id="customer-profile-form"
        onSubmit={form.handleSubmit((values) => {
          awaitingSave.current = true;
          onSubmit(values);
        })}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="customer_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer Type</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!canEdit}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="individual" id="individual" />
                    <Label htmlFor="individual">Individual</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="company" id="company" />
                    <Label htmlFor="company">Company</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {customerType === 'company' && (
          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!canEdit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="customer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{customerType === 'company' ? 'Contact Person Name' : 'Customer Name'}</FormLabel>
              <FormControl>
                <Input {...field} disabled={!canEdit} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="primary_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Primary Phone</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" disabled={!canEdit} placeholder={locale.phonePlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="secondary_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secondary Phone</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" disabled={!canEdit} placeholder={locale.phonePlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="primary_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Primary Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" disabled={!canEdit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="secondary_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secondary Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" disabled={!canEdit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="primary_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Street Address</FormLabel>
              <FormControl>
                <AddressAutocomplete
                  id="customer-primary-address"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onAddressSelected={handleAddressSelected}
                  disabled={!canEdit}
                  placeholder="Start typing a street address…"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="suburb"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{addressLabels.locality}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!canEdit} placeholder={`e.g., ${addressPlaceholders.locality}`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{addressLabels.region}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!canEdit} placeholder={`e.g., ${addressPlaceholders.region}`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="postcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{addressLabels.postcode}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!canEdit} placeholder={`e.g., ${postcodeRule.placeholder}`} maxLength={postcodeRule.maxLength} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="secondary_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Secondary Address</FormLabel>
              <FormControl>
                <Textarea {...field} disabled={!canEdit} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea {...field} disabled={!canEdit} rows={4} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {canEdit && isDirty && (
          <div className="sticky bottom-0 -mx-6 -mb-6 flex items-center justify-between gap-3 border-t bg-card px-6 py-3">
            <span className="text-sm text-muted-foreground">You have unsaved changes</span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={() => form.reset()} disabled={isSaving}>
                Discard
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </form>
    </Form>
  );
}
